import { Link } from "react-router-dom";
import VendorlyLogo from "@/components/VendorlyLogo";
import { QrCode, Users, PaperPlaneTilt, ChartLine, Storefront, ArrowRight, Sparkle, ShieldCheck, Lightning } from "@phosphor-icons/react";

const HERO_IMG = "https://images.unsplash.com/photo-1753351052363-53ce102830eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBjb2ZmZWUlMjBzaG9wJTIwY291bnRlcnxlbnwwfHx8fDE3ODU2ODM4NzN8MA&ixlib=rb-4.1.0&q=85";
const QR_IMG = "https://images.unsplash.com/photo-1600147131759-880e94a6185f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxzY2FubmluZyUyMHFyJTIwY29kZSUyMHNtYXJ0cGhvbmV8ZW58MHx8fHwxNzg1NjgzODczfDA&ixlib=rb-4.1.0&q=85";
const ENGAGE_IMG = "https://images.unsplash.com/photo-1653762379954-8943c787e78b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxjdXN0b21lciUyMHJlYWRpbmclMjB0ZXh0JTIwbWVzc2FnZSUyMHBob25lfGVufDB8fHx8MTc4NTY4Mzg3M3ww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-brand-bg text-brand-text dot-bg" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-brand-bg/70 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <VendorlyLogo size={30} />
            <span className="font-heading font-bold text-xl tracking-tight">Vendorly</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-brand-text">
            <a href="#features" className="hover:text-brand-primary transition-colors" data-testid="nav-features">Features</a>
            <a href="#how" className="hover:text-brand-primary transition-colors" data-testid="nav-how">How it works</a>
            <a href="#pricing" className="hover:text-brand-primary transition-colors" data-testid="nav-pricing">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="hidden sm:inline text-sm font-medium hover:text-brand-primary transition-colors" data-testid="landing-login">Sign in</Link>
            <Link to="/register" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors" data-testid="landing-signup">
              Get started <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-brand-border text-xs font-semibold uppercase tracking-[0.2em] text-brand-secondary mb-6" data-testid="hero-tag">
              <Sparkle size={14} weight="fill" className="text-brand-primary" /> Built for small vendors
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Turn every walk-in into a <span className="text-brand-primary">loyal customer</span>.
            </h1>
            <p className="mt-6 text-lg text-brand-text/80 max-w-xl leading-relaxed">
              Vendorly gives doctors, grocers, salons and shops a printable QR, a private customer list, and one‑tap WhatsApp campaigns — no agency required.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-6 py-3.5 font-semibold transition-colors" data-testid="hero-cta-primary">
                Start free <ArrowRight size={18} weight="bold" />
              </Link>
              <a href="#how" className="inline-flex items-center justify-center gap-2 bg-white border border-brand-border hover:border-brand-borderHover rounded-full px-6 py-3.5 font-semibold text-brand-text transition-colors" data-testid="hero-cta-secondary">
                See how it works
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.2em] text-brand-textMuted">
              <div className="flex items-center gap-2"><ShieldCheck size={16} weight="duotone" className="text-brand-secondary" /> Private by default</div>
              <div className="flex items-center gap-2"><Lightning size={16} weight="duotone" className="text-brand-secondary" /> 60‑second setup</div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-brand-border shadow-[0_10px_40px_rgba(44,42,41,0.10)] aspect-[4/5]">
              <img src={HERO_IMG} alt="Small business owner" className="w-full h-full object-cover" />
              <div className="absolute inset-0 grain mix-blend-overlay opacity-30" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-brand-border p-4 shadow-lg w-56 hidden sm:block" data-testid="hero-card-stat">
              <div className="text-xs uppercase tracking-[0.2em] text-brand-textMuted">This week</div>
              <div className="mt-1 font-heading text-2xl font-bold">+128 <span className="text-brand-primary">signups</span></div>
              <div className="text-xs text-brand-textMuted">via your counter QR</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Features</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Everything a small shop needs — nothing it doesn't.</h2>
        </div>
        <div className="grid grid-cols-12 gap-6">
          <FeatureCard span="col-span-12 md:col-span-8" title="A QR your customers actually scan" icon={QrCode}
            desc="Print it, stick it on your counter. Customers land on a branded form with just name, phone and email — no downloads."
            image={QR_IMG} tall />
          <FeatureCard span="col-span-12 md:col-span-4" title="A private customer list" icon={Users}
            desc="Search by name, filter by date. Phone numbers are masked in the UI — full number only used for sending." />
          <FeatureCard span="col-span-12 md:col-span-4" title="WhatsApp campaigns" icon={PaperPlaneTilt}
            desc="Pick a template, choose customers, hit send. Delivery and read rates tracked automatically." />
          <FeatureCard span="col-span-12 md:col-span-8" title="Simple, honest analytics" icon={ChartLine}
            desc="See signups per day, best-performing messages, and the exact delivery rate — no dashboards you'll never open."
            image={ENGAGE_IMG} tall />
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">How it works</div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">From counter to conversation in 3 steps.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Print your QR", d: "Sign up in 60 seconds. Download your unique QR and stick it on the counter." },
            { n: "02", t: "Collect customers", d: "Customers scan, fill a 20-second form, and get added to your private list." },
            { n: "03", t: "Send a message", d: "Pick a template, select customers, send via WhatsApp. Track delivery." },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-brand-border rounded-2xl p-8 hover:-translate-y-1 hover:shadow-md transition-transform duration-300" data-testid={`step-${s.n}`}>
              <div className="font-mono text-brand-primary font-semibold">{s.n}</div>
              <h3 className="mt-3 font-heading text-xl font-bold">{s.t}</h3>
              <p className="mt-2 text-brand-text/75">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing / CTA */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="bg-brand-secondary rounded-3xl p-10 sm:p-16 text-white relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-3">Free during MVP</div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Start collecting customers today.</h2>
              <p className="mt-4 text-white/85 max-w-lg">No credit card. No agency fees. Just a QR at your counter and a private list only you can see.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-6 py-3.5 font-semibold transition-colors" data-testid="cta-signup">
                Create my account <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/login" className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-6 py-3.5 font-semibold text-white transition-colors" data-testid="cta-login">
                I already have one
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-brand-border py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid gap-6 sm:grid-cols-2 md:grid-cols-4 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <VendorlyLogo size={24} />
              <span className="font-heading font-bold text-brand-text">Vendorly</span>
            </div>
            <p className="text-brand-textMuted text-xs leading-relaxed">WhatsApp CRM built for small offline shops in India. QR-to-signup in 5 seconds.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Product</div>
            <ul className="space-y-2 text-brand-text/80">
              <li><a href="#features" className="hover:text-brand-primary">Features</a></li>
              <li><a href="#how" className="hover:text-brand-primary">How it works</a></li>
              <li><a href="#pricing" className="hover:text-brand-primary">Pricing</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Company</div>
            <ul className="space-y-2 text-brand-text/80">
              <li><Link to="/about" className="hover:text-brand-primary" data-testid="footer-about">About us</Link></li>
              <li><Link to="/contact" className="hover:text-brand-primary" data-testid="footer-contact">Contact us</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-3">Legal</div>
            <ul className="space-y-2 text-brand-text/80">
              <li><Link to="/privacy" className="hover:text-brand-primary" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-brand-primary" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
              <li><Link to="/refunds" className="hover:text-brand-primary" data-testid="footer-refunds">Refund Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6 mt-6 border-t border-brand-border text-xs text-brand-textMuted flex flex-wrap items-center justify-between gap-2">
          <div>© {new Date().getFullYear()} Vendorly. Made for small shops in India.</div>
          <div>Payments by Razorpay · WhatsApp via Twilio</div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ span, title, desc, icon: Icon, image, tall }) {
  return (
    <div className={`${span} bg-white border border-brand-border rounded-2xl p-8 flex flex-col hover:-translate-y-1 hover:shadow-md transition-transform duration-300`} data-testid={`feature-${title.slice(0,10)}`}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-brand-primary/10 flex items-center justify-center">
          <Icon size={22} weight="duotone" className="text-brand-primary" />
        </div>
        <h3 className="font-heading text-xl font-bold">{title}</h3>
      </div>
      <p className="mt-3 text-brand-text/75">{desc}</p>
      {image && (
        <div className={`mt-6 rounded-xl overflow-hidden border border-brand-border ${tall ? "aspect-[16/9]" : "aspect-video"}`}>
          <img src={image} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}
